/**
 * Safe wrappers for localStorage and sessionStorage.
 *
 * On some environments (Android WebView, in-app browsers, restrictive
 * privacy settings) the global `localStorage` / `sessionStorage` objects
 * can be **null** or throw on access.  Every call site that touches
 * storage should use these wrappers instead of the raw globals to
 * prevent "Cannot read properties of null (reading 'getItem')" crashes.
 *
 * Behaviour is identical to the native API on normal browsers — the
 * wrappers simply return `null` / no-op when storage is unavailable.
 */

function getStorage(type) {
  try {
    const s = type === 'local' ? window.localStorage : window.sessionStorage
    // Probe with a test write to detect SecurityError (e.g. Safari private mode)
    if (s) {
      const testKey = '__storage_test__'
      s.setItem(testKey, '1')
      s.removeItem(testKey)
    }
    return s || null
  } catch {
    return null
  }
}

function createSafeStorage(type) {
  // Resolve the backing store lazily on first use so the module can
  // be imported at the top level even during SSR / tests.
  let _storage
  const storage = () => {
    if (_storage === undefined) _storage = getStorage(type)
    return _storage
  }

  return {
    getItem(key) {
      try { return storage()?.getItem(key) ?? null } catch { return null }
    },
    setItem(key, value) {
      try { storage()?.setItem(key, value) } catch { /* quota exceeded / blocked */ }
    },
    removeItem(key) {
      try { storage()?.removeItem(key) } catch { /* noop */ }
    },
    clear() {
      try { storage()?.clear() } catch { /* noop */ }
    },
  }
}

export const safeLocalStorage = createSafeStorage('local')
export const safeSessionStorage = createSafeStorage('session')
